/**
 * Add to Cart Button Manager
 * Handles button state for products with Size/Width options.
 *
 * Size and Width only count as chosen once the shopper picks them, or when the page was
 * opened on a ?variant= link (that link names a size on purpose). Until then the option
 * label shows no value, no box is marked, and the button asks for the missing option.
 * An option with a single value is the only choice, so it counts as chosen from the start.
 *
 * The server fills every option with its own first-available guess whenever it renders the
 * picker, on page load and after every pick. The variant picker snippets leave Size and Width
 * unmarked on page load, and SelectionState strips the guesses out of each re-rendered picker
 * before the theme swaps it in, so a value only ever appears because the shopper chose it.
 */

const GATED_OPTION_SLUGS = ["size", "width"]; // fieldset[data-option-slug]; same options buy-buttons.liquid gates on
const SELECT_PROMPTS = {
  size: "PLEASE SELECT SIZE",
  width: "PLEASE SELECT WIDTH",
};
const SELECT_PROMPT_PREFIX = "PLEASE SELECT";
const HAS_VARIANT_PARAM = new URLSearchParams(window.location.search).has(
  "variant",
);

class SelectionState {
  constructor(onChange) {
    this.chosen = new Map(); // option slug -> value the shopper picked
    this.productId = null; // the product those picks belong to
    this.onChange = onChange;
    this.seeded = false;
    this.hookedInfos = new WeakSet();
    this.observer = null;
    this.expected = null; // { productId, picks, at }: see expect()
  }

  productInfo() {
    const button = document.querySelector(".product-form__submit");
    return (
      (button && button.closest("product-info")) ||
      document.querySelector("product-info")
    );
  }

  pickerIdFor(info) {
    const section =
      info.getAttribute("data-original-section-id") ||
      info.getAttribute("data-section-id");
    return `VariantPicker-${section}-${info.getAttribute("data-product-id")}`;
  }

  // The main product's live picker. After each pick the theme keeps the old picker in the
  // page, hidden, for a moment next to the new one, so hidden ones are skipped.
  picker() {
    const info = this.productInfo();
    if (!info) return null;
    const id = this.pickerIdFor(info);
    return (
      Array.from(document.querySelectorAll("variant-picker")).find(
        (picker) => picker.id === id && picker.style.display !== "none",
      ) || null
    );
  }

  // Picks belong to one product. If the page now shows a different product (a colour
  // switch that swaps the product in place), they are dropped before anything uses them,
  // unless the switch said which of them to keep (expect()).
  checkProduct() {
    const info = this.productInfo();
    const id = info ? info.getAttribute("data-product-id") : null;
    if (this.productId !== null && id !== this.productId) {
      this.chosen.clear();
      this.productId = id;
      this.adoptExpected(id);
      return;
    }
    this.productId = id;
  }

  // assets/instant-colorway.js calls this right before it swaps in another colour (product
  // id): that product starts with these picks ([{ name, value }] by option name) instead of
  // none, so nothing on the page reacts to an empty selection while the new section is going
  // in. Values the new product doesn't offer are left out.
  expect(productId, picks) {
    this.expected =
      productId == null
        ? null
        : { productId: String(productId), picks: picks || [], at: Date.now() };
  }

  adoptExpected(id) {
    const expected = this.expected;
    if (!expected || expected.productId !== String(id)) return;
    this.expected = null;
    if (Date.now() - expected.at > 10000) return;
    const picker = this.picker();
    if (!picker) return;
    const key = (value) =>
      String(value == null ? "" : value)
        .trim()
        .toLowerCase();
    this.gatedFieldsets(picker).forEach((fieldset) => {
      const radios = Array.from(
        fieldset.querySelectorAll('input[type="radio"]'),
      );
      expected.picks.forEach((pick) => {
        const radio = radios.find(
          (r) => r.name === pick.name && key(r.value) === key(pick.value),
        );
        if (radio) this.chosen.set(this.slugOf(fieldset), radio.value);
      });
    });
  }

  slugOf(fieldset) {
    return fieldset.getAttribute("data-option-slug");
  }

  // Size/Width fieldsets that are a real choice (two or more values).
  gatedFieldsets(root) {
    if (!root) return [];
    return Array.from(
      root.querySelectorAll("fieldset[data-option-slug]"),
    ).filter(
      (fieldset) =>
        GATED_OPTION_SLUGS.includes(this.slugOf(fieldset)) &&
        fieldset.querySelectorAll('input[type="radio"]').length > 1,
    );
  }

  // A ?variant= link is a deliberate choice, so whatever the server marked for it stands.
  seed() {
    if (this.seeded) return;
    const picker = this.picker();
    if (!picker) return;
    this.seeded = true;
    if (!HAS_VARIANT_PARAM) return;

    this.gatedFieldsets(picker).forEach((fieldset) => {
      const radio = fieldset.querySelector('input[type="radio"][checked]');
      if (radio) this.chosen.set(this.slugOf(fieldset), radio.value);
    });
  }

  // Called for every change event on the page; keeps the ones that are a Size/Width pick.
  record(target) {
    if (
      !(target instanceof HTMLInputElement) ||
      target.type !== "radio" ||
      !target.checked
    )
      return false;
    const fieldset = target.closest("fieldset[data-option-slug]");
    const picker = this.picker();
    if (!fieldset || !picker || !picker.contains(fieldset)) return false;
    if (!this.gatedFieldsets(picker).includes(fieldset)) return false;

    this.checkProduct();
    this.chosen.set(this.slugOf(fieldset), target.value);
    return true;
  }

  // Makes a picker show only what the shopper chose. Runs on the live picker and on the
  // parsed HTML the theme is about to swap in; attributes are set as well as properties
  // because the theme copies the incoming picker as HTML.
  apply(root) {
    let changed = false;

    this.gatedFieldsets(root).forEach((fieldset) => {
      const slug = this.slugOf(fieldset);
      const value = this.chosen.has(slug) ? this.chosen.get(slug) : null;

      fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const on = value !== null && radio.value === value;
        if (radio.hasAttribute("checked") !== on) {
          radio.toggleAttribute("checked", on);
          changed = true;
        }
        if (radio.checked !== on) {
          radio.checked = on;
          changed = true;
        }
        const label = radio.id
          ? fieldset.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
          : null;
        if (label && label.classList.contains("checked") !== on) {
          label.classList.toggle("checked", on);
          changed = true;
        }
      });

      // The "Size: ___" value sits in a span whose id ends in -option<position>.
      const index = fieldset.getAttribute("data-option-index");
      const valueEl = index
        ? fieldset.querySelector(`.form__label [id$="-${index}"]`)
        : null;
      const shown = value === null ? "" : value;
      if (valueEl && valueEl.textContent !== shown) {
        valueEl.textContent = shown;
        changed = true;
      }
    });

    return changed;
  }

  // Brings the live picker (and the mobile size dropdown that mirrors it) in line.
  sync() {
    const picker = this.picker();
    if (!picker) return;
    this.checkProduct();
    this.seed();
    if (this.apply(picker)) {
      picker
        .querySelectorAll("size-dropdown")
        .forEach((dropdown) => dropdown.syncFromRadios?.());
    }
  }

  reset() {
    this.chosen.clear();
    this.sync();
  }

  missing() {
    const picker = this.picker();
    if (!picker) return GATED_OPTION_SLUGS[0];
    const open = this.gatedFieldsets(picker)
      .map((fieldset) => this.slugOf(fieldset))
      .filter((slug) => !this.chosen.has(slug));
    return GATED_OPTION_SLUGS.find((slug) => open.includes(slug)) || null;
  }

  isComplete() {
    return !!this.picker() && this.missing() === null;
  }

  // The theme writes ?variant= for its own guess after every pick. Until every option is
  // chosen that id is not the shopper's, so it is taken back out of the address bar; a
  // reload or a shared link then opens with nothing pre-chosen.
  tidyUrl() {
    const picker = this.picker();
    if (!picker || !this.gatedFieldsets(picker).length || this.isComplete())
      return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("variant")) return;
    url.searchParams.delete("variant");
    window.history.replaceState(window.history.state, "", url.toString());
  }

  hook() {
    // Clean the server's guesses out of the picker HTML before the theme swaps it in, so
    // they never paint. ProductInfo runs these callbacks on the incoming picker.
    document.querySelectorAll("product-info").forEach((info) => {
      if (
        this.hookedInfos.has(info) ||
        !Array.isArray(info.preProcessHtmlCallbacks)
      )
        return;
      this.hookedInfos.add(info);
      info.preProcessHtmlCallbacks.push((node) => {
        if (
          info !== this.productInfo() ||
          !node ||
          node.id !== this.pickerIdFor(info)
        )
          return;
        this.checkProduct();
        this.apply(node);
      });
    });

    // Fallback for any other way a picker gets replaced (product switch, another script):
    // mutation callbacks run before the next paint, so the guess is gone before it shows.
    if (this.observer || !("MutationObserver" in window) || !document.body)
      return;
    this.observer = new MutationObserver((records) => {
      const swapped = records.some((record) =>
        Array.from(record.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches("variant-picker") ||
              !!node.querySelector("variant-picker")),
        ),
      );
      if (!swapped) return;
      this.hook(); // a replaced product-info needs its callback too
      this.sync();
      this.onChange();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }
}

class AddToCartButtonManager {
  constructor() {
    this.mainButton = null;
    this.stickyButton = null;
    this.hasSizeOption = false;
    this.selection = new SelectionState(() => this.updateButtonState());
    this.init();
  }

  clearSizeSelection() {
    this.selection.reset();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.mainButton = document.querySelector(".product-form__submit");
    this.stickyButton = document.querySelector(".sticky_form__submit");

    if (!this.mainButton) return;

    // Check if product has size/width option
    this.hasSizeOption =
      this.mainButton.getAttribute("data-has-size-option") === "true";

    this.selection.seed();
    this.selection.hook();
    this.selection.sync();
    if (this.hasSizeOption) this.selection.tidyUrl();

    // Every Size/Width pick is recorded here (capture phase, so before the theme reacts),
    // whichever control made it: swatch, mobile dropdown or keyboard.
    document.addEventListener(
      "change",
      (event) => {
        if (this.selection.record(event.target)) {
          this.updateButtonState();
        }
      },
      true,
    );

    this.updateButtonState();

    document.addEventListener(
      "submit",
      (event) => {
        const form = this.mainButton?.form;
        if (
          !this.hasSizeOption ||
          !this.hasColorAvailability() ||
          event.target !== form
        )
          return;
        this.refresh();
        const variant = this.selection.isComplete() && this.pickedVariant();
        if (
          !variant?.available ||
          this.mainButton.disabled ||
          form.querySelector('[name="id"]')?.value !== String(variant.id)
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );

    // buy-buttons.liquid renders the label and price empty for products with a size
    // option, so the gate has to run again after every section re-render rather than
    // on a timer, otherwise the button can be left without its price.
    const pubsub = window.theme?.pubsub;
    pubsub?.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, () =>
      this.refresh(),
    );

    // The sticky bar's button is only greyed out (class disabled-btn) while a size is missing,
    // never disabled, so a click on it would submit the theme's guessed variant. dynamic.js
    // guards the button that was on the page at load; a colour switch replaces that button,
    // so every one is guarded here as well.
    const loadedStickyButton = document.querySelector(
      "#custom-sticky-cart-btn",
    );
    document.addEventListener(
      "click",
      (event) => {
        const button =
          event.target && event.target.closest
            ? event.target.closest(".sticky_form__submit")
            : null;
        if (!button || !button.classList.contains("disabled-btn")) return;
        event.preventDefault();
        if (button === loadedStickyButton) return; // dynamic.js scrolls up for this one
        const target = document.querySelector(".custom-main-product");
        if (target) target.scrollIntoView({ behavior: "smooth" });
      },
      true,
    );

    // Listen for product switch events (from color swatch handler)
    document.addEventListener("product:switched", (event) => {
      // assets/instant-colorway.js swaps the section in place and has already carried the
      // shopper's Size/Width over to the new colour when it fires this, so the switch is handled
      // at once and those picks stay. The theme's own switch clears every pick 100 ms after it.
      if (event.detail?.source === "instant-colorway")
        this.handleProductSwitch({ keepPicks: true });
      else setTimeout(() => this.handleProductSwitch(), 100);
    });
  }

  handleProductSwitch({ keepPicks = false } = {}) {
    // Re-query buttons after product switch
    this.mainButton = document.querySelector(".product-form__submit");
    this.stickyButton = document.querySelector(".sticky_form__submit");

    if (!this.mainButton) return;

    this.hasSizeOption =
      this.mainButton.getAttribute("data-has-size-option") === "true";

    this.selection.seeded = true;
    this.selection.hook();
    if (keepPicks) {
      // Picks recorded for the new product stay; any left from the previous product are dropped
      // by SelectionState.checkProduct().
      this.selection.sync();
    } else {
      // A different product: nothing has been chosen for it yet.
      this.clearSizeSelection();
    }
    this.updateButtonState();
  }

  refresh() {
    this.mainButton = document.querySelector(".product-form__submit");
    this.stickyButton = document.querySelector(".sticky_form__submit");

    if (!this.mainButton) return;

    this.hasSizeOption =
      this.mainButton.getAttribute("data-has-size-option") === "true";

    this.selection.sync();
    if (this.hasSizeOption) this.selection.tidyUrl();
    this.updateButtonState();
  }

  updateButtonState() {
    if (!this.hasSizeOption) {
      // No size option, keep default state
      return;
    }

    // Once every option is picked the section re-render is authoritative for the
    // button label, price and availability; until it lands only the label is set here.
    if (this.selection.isComplete()) {
      if (!this.hasColorAvailability()) {
        this.labelPickedVariant();
        this.restoreDefaultLabel();
        this.restorePrice();
        return;
      }
      const variant = this.pickedVariant();
      const strings = window.theme?.variantStrings;
      if (!variant) {
        this.setButtonText(strings?.unavailable || "Unavailable");
        this.disableButton();
        this.hidePrice();
        return;
      }
      const info = this.selection.productInfo();
      info
        .querySelectorAll('form[data-type="add-to-cart-form"] [name="id"]')
        .forEach((input) => {
          input.value = String(variant.id);
          input.disabled = false;
        });
      this.setButtonText(
        variant.available
          ? (this.mainButton.hasAttribute("data-pre-order")
              ? strings?.preOrder
              : strings?.addToCart) || "Add to Cart"
          : strings?.soldOut || "Sold out",
      );
      if (variant.available) this.enableButton();
      else this.disableButton();
      this.restorePrice();
      return;
    }

    this.setButtonText(
      SELECT_PROMPTS[this.selection.missing()] || SELECT_PROMPTS.size,
    );
    this.disableButton();
    this.hidePrice();
  }

  hasColorAvailability() {
    return !!this.selection
      .picker()
      ?.querySelector("script[data-color-availability]");
  }

  pickedVariant() {
    const info = this.selection.productInfo();
    const picker = this.selection.picker();
    if (!info || !picker) return null;
    const section = info.closest(".shopify-section") || document;
    const script = section.querySelector(
      'script[type="application/json"][data-product-json]',
    );
    if (!script) return null;

    let product;
    try {
      product = JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
    if (
      !product ||
      !Array.isArray(product.variants) ||
      String(product.id) !== info.getAttribute("data-product-id")
    )
      return null;

    const key = (value) =>
      String(value == null ? "" : value)
        .trim()
        .toLowerCase();
    const remembered = this.hasColorAvailability()
      ? window.AetrexColorAvailability?.wanted() || []
      : [];
    const wanted = [];
    for (const fieldset of picker.querySelectorAll(
      "fieldset[data-option-slug][data-option-index]",
    )) {
      const position = parseInt(
        String(fieldset.getAttribute("data-option-index")).replace(
          "option",
          "",
        ),
        10,
      );
      if (!position) continue;
      const slug = this.selection.slugOf(fieldset);
      let value = this.selection.chosen.has(slug)
        ? this.selection.chosen.get(slug)
        : null;
      const radio = fieldset.querySelector('input[type="radio"]');
      const wantedPick = remembered.find(
        (pick) =>
          key(pick.name) ===
          key(radio?.getAttribute("data-opt-name") || radio?.name),
      );
      if (wantedPick) value = wantedPick.value;
      if (value === null) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return null;
        value = checked.value;
      }
      wanted[position - 1] = key(value);
    }

    return (
      product.variants.find((variant) =>
        wanted.every(
          (value, index) =>
            value === undefined || key(variant.options[index]) === value,
        ),
      ) || null
    );
  }

  labelPickedVariant() {
    if (!this.mainButton || !this.mainButton.hasAttribute("loading")) return;
    const variant = this.pickedVariant();
    const strings = window.theme?.variantStrings;
    if (!variant || !strings) return;
    let text = strings.soldOut;
    if (variant.available)
      text = this.mainButton.hasAttribute("data-pre-order")
        ? strings.preOrder
        : strings.addToCart;
    const label = this.mainButton.querySelector(".btn-text > span:first-child");
    if (text && label && label.textContent.trim() !== text)
      label.textContent = text;
  }

  checkVariantAndUpdateButton(variantId) {
    // Get variant data from the page's JSON
    const variantData = this.getVariantData(variantId);

    if (variantData) {
      if (variantData.available) {
        this.setButtonText("Add to Cart");
        this.enableButton();
        this.showPrice(variantData.price);
      } else {
        this.setButtonText("Sold out");
        this.disableButton();
        this.hidePrice();
      }
    }
  }

  getVariantData(variantId) {
    // Try to get variant data from product JSON script tag
    const productJson = document.querySelector(
      'script[type="application/json"][data-product-json]',
    );
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        return product.variants.find((v) => v.id == variantId);
      } catch (e) {
        // Error parsing product JSON
      }
    }
    return null;
  }

  setButtonText(text) {
    if (this.mainButton) {
      const btnText = this.mainButton.querySelector(".btn-text");
      if (btnText) {
        let textSpan = btnText.querySelector("span:first-child");
        if (textSpan) {
          textSpan.textContent = text;
        } else {
          // Remove all text nodes
          Array.from(btnText.childNodes).forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              node.remove();
            }
          });

          textSpan = document.createElement("span");
          textSpan.textContent = text;
          // Insert at the beginning, before price div
          btnText.insertBefore(textSpan, btnText.firstChild);
        }
      }
    }

    if (this.stickyButton) {
      const btnText = this.stickyButton.querySelector(".btn-text");
      if (btnText) {
        const textSpan = btnText.querySelector("span.hidden");
        if (textSpan) {
          textSpan.textContent = text;
        } else {
          // Try without the .hidden class
          const anySpan = btnText.querySelector('span:not([class*="icon"])');
          if (anySpan) {
            anySpan.textContent = text;
          }
        }
      }
    }
  }

  enableButton() {
    if (this.mainButton) {
      this.mainButton.disabled = false;
      this.mainButton.removeAttribute("disabled");
    }

    if (this.stickyButton) {
      this.stickyButton.classList.remove("disabled-btn");
    }
  }

  disableButton() {
    if (this.mainButton) {
      this.mainButton.disabled = true;
      this.mainButton.setAttribute("disabled", "disabled");
    }

    if (this.stickyButton) {
      this.stickyButton.classList.add("disabled-btn");
    }
  }

  showPrice(price) {
    if (this.mainButton) {
      const btnText = this.mainButton.querySelector(".btn-text");
      if (!btnText) return;

      // Look for any price div (with or without ID)
      let priceDiv =
        btnText.querySelector('[id^="BuyButtonPrice-"]') ||
        btnText.querySelector("div.flex");

      if (!priceDiv) {
        // Create price div if it doesn't exist
        priceDiv = document.createElement("div");
        priceDiv.className = "flex";
        priceDiv.style.display = "flex";

        const separator = document.createElement("span");
        separator.className = "relative";
        separator.innerHTML = "&nbsp;&nbsp;-&nbsp;&nbsp;";

        const priceElement = document.createElement("product-buy-price");
        priceElement.className = "price";

        priceDiv.appendChild(separator);
        priceDiv.appendChild(priceElement);
        btnText.appendChild(priceDiv);
      } else {
        // Remove any duplicate price divs
        const allPriceDivs = btnText.querySelectorAll("div.flex");
        if (allPriceDivs.length > 1) {
          allPriceDivs.forEach((div, index) => {
            if (index > 0) {
              div.remove();
            }
          });
        }
        priceDiv.style.display = "flex";
      }

      const priceElement = priceDiv.querySelector("product-buy-price");

      if (priceElement && price) {
        priceElement.setAttribute("data-price", price);
        // Format price (assuming Shopify money format)
        const formattedPrice = this.formatMoney(price);
        priceElement.textContent = formattedPrice;
      }
    }
  }

  hidePrice() {
    if (!this.mainButton) return;

    const priceDiv = this.mainButton.querySelector('[id^="BuyButtonPrice-"]');
    if (priceDiv) {
      priceDiv.style.display = "none";
    }

    const fallback = this.mainButton.querySelector("[data-fallback-price]");
    if (fallback) {
      fallback.style.display = "none";
    }
  }

  restoreDefaultLabel() {
    if (!this.mainButton) return;

    // buy-buttons.liquid renders this label empty for products with a size option and
    // leaves it to this script, so it is filled from the server computed default only
    // when nothing else has written to it.
    const label = this.mainButton.querySelector(
      ".btn-text span[data-default-text]",
    );
    if (label && label.textContent.trim() === "") {
      label.textContent = label.getAttribute("data-default-text");
    }
  }

  restorePrice() {
    if (!this.mainButton) return;

    // After a colour switch the section re-render lags behind the click, so the price is
    // held back until the label stops asking for a size or width and the two agree. The label loses
    // its data attribute in that flow, so the first span is what has to be read here.
    const label = this.mainButton.querySelector(".btn-text > span:first-child");
    if (label && label.textContent.trim().startsWith(SELECT_PROMPT_PREFIX))
      return;

    const priceDiv = this.mainButton.querySelector('[id^="BuyButtonPrice-"]');
    if (priceDiv) {
      // The theme marks the price [hidden] for unavailable variants, but the published
      // behaviour keeps it visible next to the label, so the display is forced here.
      priceDiv.style.display = "flex";
      return;
    }

    // Switching colour makes the theme replace the whole button content, which drops the
    // server rendered price, so it is rebuilt here from the selected variant.
    this.renderFallbackPrice();
  }

  renderFallbackPrice() {
    const btnText = this.mainButton.querySelector(".btn-text");
    if (!btnText) return;

    const formatted = this.getFormattedVariantPrice();
    if (!formatted) return;

    let holder = btnText.querySelector("[data-fallback-price]");
    if (!holder) {
      holder = document.createElement("div");
      holder.className = "flex";
      holder.setAttribute("data-fallback-price", "");

      const separator = document.createElement("span");
      separator.className = "relative";
      separator.innerHTML = "&nbsp;&nbsp;-&nbsp;&nbsp;";

      const amount = document.createElement("span");
      amount.className = "price";

      holder.appendChild(separator);
      holder.appendChild(amount);
      btnText.appendChild(holder);
    }

    holder.style.display = "flex";
    holder.querySelector(".price").textContent = formatted;
  }

  getFormattedVariantPrice() {
    const script = document.querySelector(
      'script[type="application/json"][data-selected-variant]',
    );
    const currency = window.theme?.Currency;
    const settings = window.theme?.settings;
    if (!script || !currency || !settings) return null;

    try {
      const variant = JSON.parse(script.textContent);
      if (typeof variant?.price !== "number") return null;

      // Same format lookup the theme uses elsewhere, so it follows the market the shopper
      // is browsing in rather than assuming a currency.
      const format = settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat;
      return currency.formatMoney(variant.price, format);
    } catch (error) {
      return null;
    }
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your currency settings
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize
window.addToCartButtonManager = new AddToCartButtonManager();
